import * as React from "react";
import { useTranslation } from "react-i18next";
import { LineChart } from "@mui/x-charts/LineChart";

function CustomTick({ x, y, value, range }) {
  const d = value instanceof Date ? value : new Date(value);

  let label;
  if (["1H", "3H", "6H", "12H", "1D"].includes(range)) {
    label = d.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Europe/Istanbul",
    });
  } else {
    label = d.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "short",
      timeZone: "Europe/Istanbul",
    });
  }

  return (
    <text x={x} y={y} textAnchor="middle" fontSize={11} fill="#444">
      {label}
    </text>
  );
}

export default function ApparentPowerChart({ data, range, onRangeChange, isVirtual, deviceType }) {
  const { t } = useTranslation();
  const now = new Date();
  const showMarks = ["1H", "3H"].includes(range);

  let min;
  switch (range) {
    case "1H": min = new Date(now.getTime() - 1 * 60 * 60 * 1000); break;
    case "3H": min = new Date(now.getTime() - 3 * 60 * 60 * 1000); break;
    case "6H": min = new Date(now.getTime() - 6 * 60 * 60 * 1000); break;
    case "12H": min = new Date(now.getTime() - 12 * 60 * 60 * 1000); break;
    case "1D": min = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000); break;
    case "3D": min = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000); break;
    case "1W": min = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
    default: min = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  }
  const max = now;
  // Verileri kVA cinsine çevir (1000'e böl)
  const processedData = React.useMemo(() => {
    return data.map((d) => ({
      ...d,
      Smean1: d.Smean1 != null ? d.Smean1 / 1000 : null,
      Smean2: d.Smean2 != null ? d.Smean2 / 1000 : null,
      Smean3: d.Smean3 != null ? d.Smean3 / 1000 : null,
      STotalImport: d.STotalImport != null ? d.STotalImport / 1000 : null,
      STotalExport: d.STotalExport != null ? d.STotalExport / 1000 : null,
    }));
  }, [data]);

  const series = isVirtual
    ? [
      {
        dataKey: "STotalImport",
        label: t('apparentPower.totalApparentPower'),
        color: "#ff9800",
        showLine: true,
        showMark: showMarks,
        lineWidth: 3,
        valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kVA` : "-")
      },
    ]
    : deviceType === "multimeter"
      ? [
        {
          dataKey: "Smean1",
          label: t('apparentPower.L1ApparentPower'),
          color: "#e63946",
          showLine: true,
          showMark: showMarks,
          valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kVA` : "-")
        },
        {
          dataKey: "Smean2",
          label: t('apparentPower.L2ApparentPower'),
          color: "#2a9d8f",
          showLine: true,
          showMark: showMarks,
          valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kVA` : "-")
        },
        {
          dataKey: "Smean3",
          label: t('apparentPower.L3ApparentPower'),
          color: "#1d3557",
          showLine: true,
          showMark: showMarks,
          valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kVA` : "-")
        },
        {
          dataKey: "STotalImport",
          label: t('apparentPower.totalApparentPower'),
          color: "#9333ea",
          showLine: true,
          showMark: showMarks,
          lineWidth: 3,
          valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kVA` : "-")
        },
      ]
      : [
        {
          dataKey: "Smean1",
          label: t('apparentPower.L1ApparentPower'),
          color: "#e63946",
          showLine: true,
          showMark: showMarks,
          valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kVA` : "-")
        },
        {
          dataKey: "Smean2",
          label: t('apparentPower.L2ApparentPower'),
          color: "#2a9d8f",
          showLine: true,
          showMark: showMarks,
          valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kVA` : "-")
        },
        {
          dataKey: "Smean3",
          label: t('apparentPower.L3ApparentPower'),
          color: "#1d3557",
          showLine: true,
          showMark: showMarks,
          valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kVA` : "-")
        },
        {
          dataKey: "STotalImport",
          label: t('apparentPower.totalImportApparentPower'),
          color: "#9333ea",
          showLine: true,
          showMark: showMarks,
          valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kVA` : "-")
        },
        {
          dataKey: "STotalExport",
          label: t('apparentPower.totalExportApparentPower'),
          color: "#f59e0b",
          showLine: true,
          showMark: showMarks,
          valueFormatter: (v) => (v != null ? `${v.toFixed(2)} kVA` : "-")
        },
      ];

  return (
    <div>
      {/* Filtre Butonları */}
      <div className="flex flex-wrap gap-2 mb-4 justify-center md:justify-start">
        {[
          { key: "1H", label: t('timeRanges.1H') },
          { key: "3H", label: t('timeRanges.3H') },
          { key: "6H", label: t('timeRanges.6H') },
          { key: "12H", label: t('timeRanges.12H') },
          { key: "1D", label: t('timeRanges.1D') },
          { key: "3D", label: t('timeRanges.3D') },
          { key: "1W", label: t('timeRanges.1W') },
        ].map((r) => (
          <button
            key={r.key}
            onClick={() => onRangeChange(r.key)}
            className={`px-3 py-2 rounded-md border text-sm font-medium transition
              ${range === r.key
                ? "bg-red-600 text-white border-red-600"
                : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <LineChart
        dataset={processedData}
        margin={{ left: 50, right: 30, top: 20, bottom: 30 }}
        xAxis={[
          {
            dataKey: "time",
            scaleType: "time",
            position: "bottom",
            min: min.getTime(),
            max: max.getTime(),
            tickNumber: 8,
            tickLabel: (props) => <CustomTick {...props} range={range} />,
            valueFormatter: (value) => {
              const d = value instanceof Date ? value : new Date(value);
              if (["1H", "3H", "6H", "12H", "1D"].includes(range)) {
                return d.toLocaleTimeString("tr-TR", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                  timeZone: "Europe/Istanbul",
                });
              } else {
                return d.toLocaleDateString("tr-TR", {
                  day: "2-digit",
                  month: "short",
                  timeZone: "Europe/Istanbul",
                });
              }
            },
          },
        ]}
        yAxis={[
          {
            label: "kVA",
            valueFormatter: (v) => `${v.toLocaleString("tr-TR")}`,
          },
        ]}
        series={series}
        height={400}
        axisHighlight={{ x: "line" }}
        slotProps={{
          legend: {
            direction: 'row',
            position: { vertical: 'top', horizontal: 'middle' },
            padding: 0,
          },
          tooltip: { trigger: "axis" },
        }}
      />
    </div>
  );
}
